/**
 * Scenario Comparison Page
 * Compare multiple scenarios side-by-side
 */

import { Card } from '@/components/common/Card';
import { Button } from '@fluentui/react-components';
import { Add24Regular } from '@fluentui/react-icons';
import { mockScenarios } from '@/services/mockData';
import { formatCurrency, formatPercentage } from '@/utils/formatters';

export function Scenarios() {
  return (
    <div className="scenarios p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Scenario Comparison</h1>
          <p className="text-gray-600">Compare different assumptions side-by-side</p>
        </div>
        <Button appearance="primary" icon={<Add24Regular />}>
          Create New Scenario
        </Button>
      </header>

      {/* Scenario Comparison Table */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Key Metrics Comparison</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Metric</th>
                {mockScenarios.map((scenario) => (
                  <th key={scenario.id} className="px-4 py-3 text-center">
                    <div className="font-semibold">{scenario.name}</div>
                    <div className="text-xs text-gray-600 font-normal">{scenario.description}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr className="bg-blue-50">
                <td className="px-4 py-3 font-semibold" colSpan={mockScenarios.length + 1}>
                  Return Metrics
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2">Levered IRR</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center font-semibold">
                    {formatPercentage(s.results.returnMetrics.leveredIRR)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Unlevered IRR</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {formatPercentage(s.results.returnMetrics.unleveredIRR)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Equity Multiple</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {s.results.returnMetrics.equityMultiple.toFixed(2)}x
                  </td>
                ))}
              </tr>

              <tr className="bg-blue-50">
                <td className="px-4 py-3 font-semibold" colSpan={mockScenarios.length + 1}>
                  Project Metrics
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2">Development Spread</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center font-semibold">
                    {formatCurrency(s.results.developmentSpread)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Total Project Cost</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {formatCurrency(s.results.totalProjectCost)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Stabilized NOI</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {formatCurrency(s.results.stabilizedNOI)}
                  </td>
                ))}
              </tr>

              <tr className="bg-blue-50">
                <td className="px-4 py-3 font-semibold" colSpan={mockScenarios.length + 1}>
                  Key Assumptions
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2">Vacancy Rate</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {formatPercentage(s.assumptions.vacancyRate)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Exit Cap Rate</td>
                {mockScenarios.map((s) => (
                  <td key={s.id} className="px-4 py-2 text-center">
                    {formatPercentage(s.assumptions.exitCapRate)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Scenario Details Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {mockScenarios.map((scenario) => (
          <Card key={scenario.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">{scenario.name}</h3>
                <p className="text-sm text-gray-600">{scenario.description}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded">
                <div className="text-xs text-gray-600">Levered IRR</div>
                <div className="text-2xl font-bold">{formatPercentage(scenario.results.returnMetrics.leveredIRR)}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600">Equity Multiple</div>
                  <div className="font-semibold">{scenario.results.returnMetrics.equityMultiple.toFixed(2)}x</div>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600">Dev Spread</div>
                  <div className="font-semibold">{formatCurrency(scenario.results.developmentSpread)}</div>
                </div>
              </div>

              <div className="text-xs space-y-1 pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-gray-600">Vacancy:</span>
                  <span>{formatPercentage(scenario.assumptions.vacancyRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Exit Cap:</span>
                  <span>{formatPercentage(scenario.assumptions.exitCapRate)}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <Button size="small" appearance="secondary">Edit</Button>
              <Button size="small" appearance="subtle">Duplicate</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
